"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { ArrowRight } from "@/components/icons";
import { GREEN, ROUTES } from "@/lib/constants";
import { products } from "./data";
import SectionHeader from "@/components/ui/section-header";
import dynamic from "next/dynamic";

const WaitlistModal = dynamic(() => import("./waitlist-modal"), {
  ssr: false,
  loading: () => null,
});
const IspWaitlistModal = dynamic(() => import("./isp-waitlist-modal"), {
  ssr: false,
  loading: () => null,
});

type WaitlistProduct = "datacenter" | "isp";

// Card content slides in from the right, staggered by card index.
const contentVariants = {
  hidden: { opacity: 0, x: 40 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
      delay: i * 0.12,
    },
  }),
};

export default function ProductsSection() {
  const reduced = useReducedMotion() ?? false;
  const [activeId, setActiveId] = useState<string>("mobile");
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [waitlistProduct, setWaitlistProduct] = useState<WaitlistProduct | null>(null);

  const sliderRef = useRef<HTMLDivElement>(null);
  const [isSliderMode, setIsSliderMode] = useState(false);
  const [isMobileStacked, setIsMobileStacked] = useState(false);
  const [snappedIdx, setSnappedIdx] = useState(0);

  useEffect(() => {
    const mqSlider = window.matchMedia("(min-width: 640px) and (max-width: 1023px)");
    const mqMobile = window.matchMedia("(max-width: 639px)");
    const update = () => {
      setIsSliderMode(mqSlider.matches);
      setIsMobileStacked(mqMobile.matches);
    };
    update();
    mqSlider.addEventListener("change", update);
    mqMobile.addEventListener("change", update);
    return () => {
      mqSlider.removeEventListener("change", update);
      mqMobile.removeEventListener("change", update);
    };
  }, []);

  useEffect(() => {
    if (!isSliderMode) return;
    const el = sliderRef.current;
    if (!el) return;
    const onScroll = () => {
      const center = el.scrollLeft + el.clientWidth / 2;
      let bestIdx = 0;
      let bestDist = Infinity;
      for (let i = 0; i < el.children.length; i++) {
        const c = el.children[i] as HTMLElement;
        const cCenter = c.offsetLeft + c.offsetWidth / 2;
        const dist = Math.abs(center - cCenter);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = i;
        }
      }
      setSnappedIdx(bestIdx);
    };
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [isSliderMode]);

  const router = useRouter();

  const openWaitlist = (productId: string) => {
    if (productId === "isp") setWaitlistProduct("isp");
    else setWaitlistProduct("datacenter");
  };
  const closeWaitlist = () => setWaitlistProduct(null);

  return (
    <section
      id="products"
      className="py-section border-border border-b"
      aria-labelledby="products-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 max-w-2xl sm:mb-16">
          <SectionHeader
            eyebrow="Our Products"
            id="products-heading"
            heading="Mobile and residential proxies. Datacenter and ISP coming soon."
            body={
              <>
                Get reliable proxy infrastructure for web scraping, automation, market research, AI
                data collection, and more. Available now:{" "}
                <strong className="text-foreground font-semibold">
                  mobile proxies and residential proxies
                </strong>
                , with flexible targeting and global coverage. Datacenter and static ISP proxies are
                on the way — join the waitlist below for early access.
              </>
            }
          />
          <p className="text-muted-foreground mt-4 text-sm leading-relaxed">
            Our{" "}
            <Link
              href={ROUTES.productMobileProxies}
              className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
            >
              managed mobile proxies
            </Link>{" "}
            and{" "}
            <Link
              href={ROUTES.productResidentialProxies}
              className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
            >
              residential proxies
            </Link>{" "}
            are both free to sign up for.{" "}
            <Link
              href="/pricing"
              className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
            >
              View pricing
            </Link>{" "}
            to compare plans.
          </p>
        </div>

        <div
          ref={sliderRef}
          className={cn(
            // real gaps, not hairline seams — each card carries its own border
            "flex flex-col gap-4 sm:gap-5",
            "sm:scrollbar-hide sm:snap-x sm:snap-mandatory sm:flex-row sm:overflow-x-auto",
            "lg:grid lg:snap-none lg:grid-cols-3 lg:grid-rows-2 lg:items-stretch lg:overflow-visible"
          )}
        >
          {products.map((product, i) => {
            const { Visual } = product;
            const isSnapped = isSliderMode && snappedIdx === i;
            const isActive = isMobileStacked
              ? true
              : isSliderMode
                ? isSnapped
                : product.id === activeId;
            const isVisualHovered = isMobileStacked
              ? true
              : isSliderMode
                ? isSnapped
                : hoveredId === product.id;
            const isCompactDesktop = product.id === "datacenter" || product.id === "isp";
            const placementClass =
              product.id === "mobile"
                ? "lg:col-start-1 lg:row-start-1 lg:row-span-2"
                : product.id === "residential"
                  ? "lg:col-start-2 lg:row-start-1 lg:row-span-2"
                  : product.id === "datacenter"
                    ? "lg:col-start-3 lg:row-start-1"
                    : "lg:col-start-3 lg:row-start-2";
            const ctaClassName = cn(
              buttonVariants({ variant: isActive ? "default" : "outline", size: "sm" }),
              "w-fit font-medium mt-1 transition-all duration-300"
            );
            return (
              <motion.div
                key={product.id}
                onHoverStart={() => {
                  setActiveId(product.id);
                  setHoveredId(product.id);
                }}
                onHoverEnd={() => {
                  setActiveId("mobile");
                  setHoveredId(null);
                }}
                onClick={() => {
                  if (product.comingSoon) openWaitlist(product.id);
                  else router.push(product.href);
                }}
                className={cn(
                  "bg-card card-wash border-border relative flex cursor-pointer flex-col overflow-hidden rounded-2xl border",
                  "w-full sm:w-[85%] sm:shrink-0 sm:snap-start lg:w-auto lg:shrink",
                  placementClass,
                  "transition-all duration-300",
                  isActive && "ring-brand/20 ring-1 ring-inset"
                )}
              >
                {product.badge && (
                  <span
                    className="bg-background/70 absolute top-3 right-3 z-10 inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold tracking-wider uppercase backdrop-blur-sm"
                    style={{
                      color: "var(--brand-green)",
                      borderColor: "color-mix(in srgb, var(--brand-green) 33%, transparent)",
                    }}
                  >
                    {product.badge}
                  </span>
                )}

                <div className={cn("h-[220px] w-full", isCompactDesktop && "lg:h-[110px]")}>
                  <Visual reduced={reduced} hovered={isVisualHovered} />
                </div>

                <motion.div
                  variants={contentVariants}
                  custom={i}
                  initial={reduced ? "visible" : "hidden"}
                  whileInView="visible"
                  viewport={scrollRevealViewport}
                  className={cn(
                    "flex flex-1 flex-col gap-3 px-6 py-6",
                    isCompactDesktop && "lg:gap-2 lg:px-5 lg:py-4"
                  )}
                >
                  <h3 className="text-foreground text-lg font-semibold">{product.name}</h3>
                  <p
                    className={cn(
                      "text-muted-foreground text-sm leading-relaxed",
                      isCompactDesktop && "lg:text-xs"
                    )}
                  >
                    {product.description}
                  </p>
                  <ul
                    className={cn("flex flex-1 flex-col gap-1.5", isCompactDesktop && "lg:hidden")}
                  >
                    {product.bullets.map((b) => (
                      <li key={b} className="text-muted-foreground flex items-center gap-2 text-sm">
                        <span
                          className="h-1 w-1 shrink-0 rounded-full"
                          style={{ backgroundColor: GREEN }}
                        />
                        {b}
                      </li>
                    ))}
                  </ul>
                  {isCompactDesktop && <div className="hidden lg:block lg:flex-1" />}
                  {product.comingSoon ? (
                    <button
                      type="button"
                      onClick={() => openWaitlist(product.id)}
                      className={ctaClassName}
                    >
                      {product.cta}
                      <ArrowRight size={13} className="ml-1.5" aria-hidden="true" />
                    </button>
                  ) : (
                    <Link href={product.href} className={ctaClassName}>
                      {product.cta}
                      <ArrowRight size={13} className="ml-1.5" aria-hidden="true" />
                    </Link>
                  )}
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <WaitlistModal open={waitlistProduct === "datacenter"} onClose={closeWaitlist} />
      <IspWaitlistModal open={waitlistProduct === "isp"} onClose={closeWaitlist} />
    </section>
  );
}
